import React from "react";
import {
  useWSModalCustom,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSInfoBox,
  WSList,
  WSModal,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IRequirementResponse,
  RequirementStatus
} from "@wingspanhq/payments/dist/interfaces";
import {
  ContextItem,
  RequirementAdditionalData,
  RequirementStatusViewProps
} from "../../types";
import { useUpdatePayeeEngagementRequirement } from "../../../../query/payeeEngagements/mutations/useUpdatePayeeEngagementRequirement";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { usePayeeRowsSummaryQuery } from "../../../../query/search/payee/queries/usePayeeRowsSummaryQuery";
import { RequirementReviewStrategy } from "@wingspanhq/payments/dist/interfaces/requirement";

export interface IRequirementWithEngagementIdsResponse
  extends IRequirementResponse {
  relatedEngagementRequirementIds: {
    engagementId: string;
    relatedRequirementId: string;
  }[];
}

export interface MarkRequirementAsCompleteModalProps {
  onClose: () => void;
  requirement: IRequirementResponse;
  context: ContextItem;
  additionalData?: RequirementAdditionalData;
  currentStatus: RequirementStatusViewProps;
  onSuccess: () => void;
}

export const MarkRequirementAsCompleteModal: React.FC<
  MarkRequirementAsCompleteModalProps
> = ({ onClose, requirement, context, onSuccess, currentStatus }) => {
  const [changeRequirementStatus, changeRequirementStatusMeta] =
    useUpdatePayeeEngagementRequirement();

  const queryPayeeGeneralSummary = usePayeeRowsSummaryQuery({
    filter: {
      "engagements.requirements.requirementId": requirement.requirementId
    }
  });

  const onSubmit = () => {
    changeRequirementStatus(
      {
        payeeId: requirement.payeeId,
        engagementId: requirement.payerPayeeEngagementIds[0]!,
        requirementId: requirement.requirementId,
        request: { status: RequirementStatus.Complete }
      },
      {
        onSuccess() {
          onSuccess();
          onClose();
        }
      }
    );
  };

  const contractorsLength = queryPayeeGeneralSummary.data?.listSize || "-";

  const isApprovalFlow =
    requirement.reviewStrategy === RequirementReviewStrategy.AlwaysRequired ||
    requirement.reviewStrategy === RequirementReviewStrategy.OnFail;

  return (
    <WSModal
      title={
        isApprovalFlow ? "Approve requirement" : "Mark requirement as complete"
      }
      onClose={onClose}
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Secondary",
            label: "Cancel",
            onClick: onClose,
            type: "button"
          },
          {
            kind: "Primary",
            label: isApprovalFlow ? "Yes, approve" : "Yes, mark complete",
            onClick: onSubmit,
            type: "submit",
            loading: changeRequirementStatusMeta.isLoading
          }
        ]
      }}
    >
      <WSList gap="L">
        <WSFlexBox wrap="nowrap" gap="M">
          <WSElement>
            <WSText.ParagraphSm color="gray600" mb="S">
              Current status:
            </WSText.ParagraphSm>
            <WSPill {...currentStatus.pill}></WSPill>
            <WSText.ParagraphXs mt="XS" ml="S" color="gray400">
              {currentStatus.secondaryText}
            </WSText.ParagraphXs>
          </WSElement>
          <WSIcon
            style={{
              marginTop: "auto",
              marginBottom: "auto"
            }}
            name="arrow-right"
          />

          <WSElement>
            <WSText.ParagraphSm color="gray600" mb="S">
              Overwritten status preview:
            </WSText.ParagraphSm>
            <WSPill text="Complete" theme="success" icon></WSPill>
            <WSText.ParagraphXs mt="XS" ml="S" color="gray400">
              {isApprovalFlow ? "Approved by you" : "Marked complete by you"}
            </WSText.ParagraphXs>
          </WSElement>
        </WSFlexBox>
        <WSInfoBox py="L">
          <WSText.ParagraphSm color="gray600">
            By proceeding, the requirement status will be marked complete by
            you.
            <ul>
              <li>The status will be updated to “Complete”</li>
            </ul>
          </WSText.ParagraphSm>

          <WSText.ParagraphSm color="gray600">
            This requirement is assigned to {contractorsLength} of the
            contractor’s engagements and may affect their eligibility to receive
            payments from you. Would you like to proceed?
          </WSText.ParagraphSm>
        </WSInfoBox>
        <WSErrorMessage
          mt="XL"
          contextKey="Other"
          error={changeRequirementStatusMeta.isError}
        />
      </WSList>
    </WSModal>
  );
};

export const useMarkRequirementAsCompleteModal = () =>
  useWSModalCustom(MarkRequirementAsCompleteModal);
